import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, UserCircle2, Mail } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";

const LAST_UPDATED = "8 de febrero de 2026";

export default function AvisoPrivacidad() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <header className="max-w-3xl mx-auto w-full px-5 pt-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900"
          data-testid="aviso-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Inicio
        </button>
        <BrandMark size="sm" />
      </header>

      <div className="max-w-3xl mx-auto w-full px-5 pt-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-700 flex items-center justify-center">
            <Lock className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-black text-blue-700">Documento legal</p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 leading-tight">
              Aviso de Privacidad
            </h1>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mb-8">Última actualización: {LAST_UPDATED}</p>

        <div className="prose prose-zinc max-w-none space-y-6 text-zinc-800 leading-relaxed">

          <Section title="1. Identidad y domicilio del responsable" testId="aviso-section-responsable">
            <p>
              <strong>MARKETING DE INTELIGENCIA ARTIFICIAL S. de R.L. de C.V.</strong> (en adelante, «la Empresa»), con domicilio en <strong>Av. Américas 1545, pisos 20, 21 y 22, Guadalajara, Jalisco, México</strong>, es la responsable del tratamiento de los datos personales que se obtengan a través de la plataforma <strong>miapoyo.com · Defiende Tus Derechos</strong>.
            </p>
            <div className="bg-white border-2 border-blue-200 rounded-xl p-4 mt-3">
              <div className="flex items-center gap-2 mb-1">
                <UserCircle2 className="w-4 h-4 text-blue-700" />
                <p className="text-[10px] uppercase tracking-widest font-black text-blue-700">Responsable interno del tratamiento de datos</p>
              </div>
              <p className="font-bold text-zinc-900 not-prose">RICARDO ZERMEÑO MEJÍA</p>
              <p className="text-xs text-zinc-600 not-prose">Av. Américas 1545, pisos 20, 21 y 22, Guadalajara, Jalisco, México</p>
            </div>
          </Section>

          <Section title="2. Datos personales que recabamos">
            <p>Para brindarte nuestros servicios recabamos únicamente:</p>
            <p>a) <strong>Pseudónimo (nombre inventado).</strong> Por diseño no solicitamos tu nombre real para proteger tu integridad migratoria.</p>
            <p>b) <strong>Contraseña</strong> almacenada cifrada con bcrypt. Nunca tenemos acceso a tu contraseña en texto plano.</p>
            <p>c) <strong>PIN numérico de uso</strong> generado automáticamente al confirmarse tu pago.</p>
            <p>d) <strong>Contenido de tus conversaciones</strong> con el asistente de voz (texto transcrito y audio temporal).</p>
            <p>e) <strong>Datos de transacción</strong> cuando realizas un pago: monto, método (Stripe / Western Union / MoneyGram / Remitly), MTCN o ID de sesión, y estado del pago.</p>
            <p>f) <strong>Datos técnicos</strong>: dirección IP (para rate limiting anti-fraude), navegador y sistema operativo.</p>
            <p className="bg-emerald-50 border-l-4 border-emerald-600 p-3 rounded-r not-prose">
              <strong className="text-emerald-800">No solicitamos:</strong> nombre real, fecha de nacimiento, número de pasaporte, número A, dirección física, correo electrónico personal, ni datos de tu situación migratoria.
            </p>
          </Section>

          <Section title="3. Finalidades del tratamiento">
            <p><strong>Finalidades primarias</strong> (sin tu consentimiento expreso adicional):</p>
            <p>I. Brindarte el servicio educativo e informativo de la plataforma.</p>
            <p>II. Procesar el pago de tu plan y otorgarte tu PIN único.</p>
            <p>III. Mantener la seguridad de la cuenta (rate limiting, prevención de fraude).</p>
            <p>IV. Generar transcripciones temporales de tus llamadas para que el asistente de IA pueda responder.</p>
            <p><strong>Finalidades secundarias</strong> (puedes oponerte):</p>
            <p>V. Estadísticas anónimas agregadas para mejorar el servicio.</p>
          </Section>

          <Section title="4. Transferencias de datos">
            <p>
              Tus datos NUNCA son compartidos con autoridades migratorias estadounidenses (ICE, USCIS, CBP), mexicanas, ni con ninguna autoridad gubernamental, salvo orden judicial expresa de autoridad competente conforme a la ley aplicable.
            </p>
            <p>
              Para operar el servicio, transferimos datos estrictamente necesarios a los siguientes encargados:
            </p>
            <p>• <strong>OpenAI / Emergent LLM</strong>: contenido de tus conversaciones para procesar voz e IA (sujeto a sus políticas de no entrenamiento con datos de cliente).</p>
            <p>• <strong>Stripe Inc.</strong>: datos de tarjeta y monto cuando eliges pagar con tarjeta.</p>
            <p>• <strong>MongoDB Atlas</strong>: almacenamiento cifrado de la base de datos.</p>
          </Section>

          <Section title="5. Derechos ARCO">
            <p>
              Conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (México), tienes en todo momento los siguientes derechos:
            </p>
            <p>• <strong>Acceso</strong>: conocer qué datos tuyos tenemos.</p>
            <p>• <strong>Rectificación</strong>: solicitar corrección de datos inexactos.</p>
            <p>• <strong>Cancelación</strong>: solicitar la eliminación de tus datos.</p>
            <p>• <strong>Oposición</strong>: oponerte al tratamiento para finalidades específicas.</p>
            <p>
              Para ejercerlos envía solicitud al responsable interno <strong>Ricardo Zermeño Mejía</strong> al domicilio indicado, o a través del formulario de contacto que habilitemos en la plataforma. Daremos respuesta en un plazo no mayor a 20 días hábiles.
            </p>
          </Section>

          <Section title="6. Plazo de conservación">
            <p>
              Conservamos tus datos mientras tu cuenta esté activa. Las transcripciones de audio se eliminan automáticamente 30 días después de cada llamada. Los datos de transacción se conservan 10 años por obligación fiscal y contable.
            </p>
          </Section>

          <Section title="7. Medidas de seguridad">
            <p>
              Aplicamos cifrado en tránsito (HTTPS/TLS), cifrado de contraseñas (bcrypt), rate limiting anti-brute-force sobre el PIN, registros de auditoría y respaldos cifrados. No obstante, ningún sistema es 100% inviolable; te recomendamos no compartir tu PIN.
            </p>
          </Section>

          <Section title="8. Cambios al aviso de privacidad">
            <p>
              Cualquier modificación a este aviso será publicada en esta misma página con la fecha de actualización. Es responsabilidad del usuario revisarlo periódicamente.
            </p>
          </Section>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mt-8">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-blue-700" />
              <h3 className="font-black text-zinc-900 not-prose">Contacto del responsable</h3>
            </div>
            <p className="not-prose text-sm">
              <strong>Ricardo Zermeño Mejía</strong>
              <br />
              MARKETING DE INTELIGENCIA ARTIFICIAL S. de R.L. de C.V.
              <br />
              Av. Américas 1545, pisos 20, 21 y 22
              <br />
              Guadalajara, Jalisco, México
            </p>
          </div>
        </div>
      </div>
      <DisclaimerBanner />
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId}>
      <h2 className="text-xl sm:text-2xl font-black text-zinc-900 mt-6 mb-2 tracking-tight">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
